import authSession from "../lib/authSession";



import { Mail, Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect } from "react";
import Logo from "../assets/fillopsLogo.png";
import AuthArtwork from "../assets/learning.jpeg";
import { useToast } from "../components/ui/use-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_URL } from "../constants";
import { toDisplayString, getErrorMessage } from "../utils/toDisplayString";
import { goAfterLogin, roleFromToken, roleFromUser } from "../lib/roles";
import GoogleOneTap from "../components/GoogleOneTap";
import { useUserAuth } from "../store/user";

const loginSchema = z.object({
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(2, "Password must be at least 6 characters"),
});
const RETURN_TO_KEY = "fillop_auth_return_to";
const LAST_LOGIN_METHOD_KEY = "fillop_last_login_method";

function rememberLoginMethod(method) {
  try { localStorage.setItem(LAST_LOGIN_METHOD_KEY, method); } catch { /* optional convenience */ }
}

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { userGoogleLogin } = useUserAuth();
  const returnTo = searchParams.get("next") || localStorage.getItem(RETURN_TO_KEY);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [lastLoginMethod] = useState(() => {
    try { return localStorage.getItem(LAST_LOGIN_METHOD_KEY); } catch { return null; }
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
  });

  // Show toast if redirected back after email verification
  useEffect(() => {
    if (searchParams.get("verified") === "true") {
      toast({
        title: "Email Verified 🎉",
        description: "You can now log in to your account.",
      });
    }
  }, [searchParams, toast]);

  // Show toast if redirected back after a successful password reset
  useEffect(() => {
    if (searchParams.get("reset") === "true") {
      toast({
        title: "Password reset 🎉",
        description: "Sign in with your new password.",
      });
    }
  }, [searchParams, toast]);

  const onSubmit = async (formData) => {
    setError("");
    setLoading(true);
    try {
      // Cookie-only: the API sets the auth cookies on this request.
      const response = await authSession.authFetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email, password: formData.password }),
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        // data.message / data.error may be a nested object ({message: "..."}),
        // so normalize to a string before storing in state / rendering it.
        const rawError = data.message || data.error;
        const errorMsg = toDisplayString(rawError, "Something went wrong");
        setError(errorMsg);
        if (data.error === "Email not verified." || errorMsg === "Email not verified.") {
          localStorage.setItem("optEmail", formData.email);
          localStorage.setItem("email", formData.email);
          navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
        }
        return;
      }

      // Auth endpoint = cookie-based, and the body carries the tokens:
      //   { user, accessToken, refreshToken, role }
      // The role is read from the signed token claim (falling back to the body),
      // never guessed from the email address.
      const accessToken = authSession.findAccessToken(data) || null;
      const user = data.user || data.data?.user || null;
      const session = authSession.setSession({
        token: accessToken,
        refreshToken: data.refreshToken || data.data?.refreshToken || null,
        user,
      });
      const signedInRole = session.role || roleFromToken(accessToken) || roleFromUser(user) || "user";
      rememberLoginMethod("password");

      // One shared helper keeps the navbars, the guards and the redirects in sync.
      authSession.persistIdentity(signedInRole, {email: formData.email, user});

      toast({ title: "Login successful! 👋", description: "Welcome back." });

      // Redirect by role; `?next=` is honoured only inside that role's area.
      localStorage.removeItem(RETURN_TO_KEY);
      goAfterLogin(navigate, {role: signedInRole, next: returnTo});

    } catch (err) {
      console.error("Login error:", err);
      setError(getErrorMessage(err, "Server error, please try again later."));
    } finally {
      setLoading(false);
    }
  };

  // Google popup → GIS gives us a credential; exchange it at POST /api/auth/google.
  // `userGoogleLogin` sets the cookie session + store state (see userAuthStore).
  const handleGoogleSuccess = async (response) => {
    setError("");
    const result = await userGoogleLogin(response.credential);
    if (!result.success) {
      setError(result.error || "Google sign-in failed. Please try again.");
      return;
    }
    const signedInRole = "user";
    rememberLoginMethod("google");
    authSession.persistIdentity(signedInRole, { email: result.user?.email, user: result.user });
    toast({ title: "Login successful! 👋", description: "Welcome back." });
    localStorage.removeItem(RETURN_TO_KEY);
    goAfterLogin(navigate, { role: signedInRole, next: returnTo });
  };

  const handleGoogleError = (err) => {
    setError(getErrorMessage(err, "Google sign-in failed. Please try again."));
  };

  return (
    <section className="min-h-screen min-h-[100dvh] bg-slate-50 dark:bg-slate-950 px-0 py-0 sm:px-6 sm:py-6 lg:p-10 transition-colors duration-300">
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="mx-auto grid min-h-[100dvh] w-full min-w-0 max-w-6xl overflow-hidden bg-white shadow-none sm:min-h-0 sm:rounded-xl sm:shadow-2xl sm:shadow-slate-900/10 lg:min-h-[680px] lg:grid-cols-[0.95fr_1.05fr]"
      >
        <aside className="relative hidden min-h-full overflow-hidden bg-[#142342] lg:block">
          <img src={AuthArtwork} alt="Learners studying together" className="absolute inset-0 h-full w-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#101a32] via-[#142342]/35 to-[#142342]/10" />
          <div className="relative flex h-full min-h-[680px] flex-col justify-between p-10 xl:p-14">
            <img src={Logo} alt="Fillop" className="h-14 w-14 rounded-2xl object-cover p-1 shadow-lg" />
            <div className="max-w-md text-white">
              <span className="mb-4 inline-flex rounded-full border border-white/30 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em]">Fillop learning</span>
              <h2 className="text-4xl font-bold leading-tight xl:text-5xl">Keep learning. Keep moving forward.</h2>
              <p className="mt-4 max-w-sm text-base leading-7 text-white/80">Your courses, learning resources, and next opportunity are waiting for you.</p>
            </div>
            <p className="text-xs text-white/70">Learn at your pace, wherever you are.</p>
          </div>
        </aside>

        <div className="flex min-w-0 items-start justify-center px-4 pb-5 pt-[max(1rem,env(safe-area-inset-top))] sm:items-center sm:px-10 sm:py-8 lg:px-12 xl:px-16">
        <div className="w-full min-w-0 max-w-md">

        {/* Logo */}
        <div className="mb-4 flex items-center gap-3 lg:hidden">
          <img src={Logo} alt="Fillop" className="h-10 w-10 rounded-xl object-cover" />
          <span className="font-bold text-slate-900 dark:text-white">Fillop LMS</span>
        </div>

        {/* Header */}
        <div className="mb-4 text-left">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Welcome Back</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Sign in to continue</p>
        </div>

        {/* Google sign-in is the first option. */}
        <div className="mb-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Continue with</span>
            {lastLoginMethod === "google" && (
              <span className="rounded bg-indigo-600 px-2 py-1 text-xs font-medium text-white">Last used</span>
            )}
          </div>
          <GoogleOneTap onSuccess={handleGoogleSuccess} onError={handleGoogleError} enabled={!loading} autoPrompt={false} renderButton width={400} />
        </div>

        <div className="mb-4 flex items-center gap-3">
          <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
          <span className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
            or sign in with email
            {lastLoginMethod === "password" && (
              <span className="rounded bg-indigo-600 px-2 py-1 font-medium text-white">Last used</span>
            )}
          </span>
          <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          {/* Email */}
          <div className="relative">
            <Mail className="absolute left-3 top-3.5 text-gray-400 h-5 w-5" />
            <input
              type="email"
              placeholder="Email Address"
              {...register("email")}
              className={`w-full pl-10 pr-3 py-2.5 border rounded-lg text-gray-700 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 bg-transparent focus:outline-none focus:ring-2 focus:ring-indigo-400 transition ${
                errors.email ? "border-red-400" : "border-gray-300 dark:border-gray-700"
              }`}
            />
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>}
          </div>

          {/* Password */}
          <div className="relative">
            <Lock className="absolute left-3 top-3.5 text-gray-400 h-5 w-5" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              {...register("password")}
              className={`w-full pl-10 pr-10 py-2.5 border rounded-lg text-gray-700 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 bg-transparent focus:outline-none focus:ring-2 focus:ring-indigo-400 transition ${
                errors.password ? "border-red-400" : "border-gray-300 dark:border-gray-700"
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(p => !p)}
              className="absolute right-3 top-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
            {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password.message}</p>}
          </div>

          {/* Error */}
          {error && <p className="text-red-500 text-sm text-center">{error}</p>}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full flex items-center justify-center gap-2 ${
              loading ? "bg-[#65718a] cursor-not-allowed" : "bg-[#16213A] hover:bg-[#10192c]"
            } text-white font-semibold py-2.5 rounded-lg shadow-md transition duration-200`}
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Logging in...</> : "Log In"}
          </button>

          {/* Links */}
          <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-3 text-xs text-gray-600 dark:text-gray-400">
            <p
              onClick={() => navigate("/forgot-password")}
              className="hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer"
            >
              Forgot Password?
            </p>
            <p
              onClick={() => navigate(returnTo ? `/register?next=${encodeURIComponent(returnTo)}` : "/register")}
              className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:underline cursor-pointer"
            >
              Create Account
            </p>
          </div>
        </form>

        <p className="text-xs text-gray-500 text-center mt-3">
          By signing in, you agree to our{" "}
          <a href="/terms#using-filloptech" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
            Terms of Use
          </a>
        </p>

        <p className="text-center text-[10px] text-gray-400 dark:text-gray-500 mt-4">
          © {new Date().getFullYear()} CBT Project. All rights reserved.
        </p>
        </div>
        </div>
      </motion.div>
    </section>
  );
};

export default Login;
